// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## PianoSVG
//
// Interactive for displaying & getting answers on a piano keyboard.
import type {
  NoteName,
  LetterName,
  Accidental,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import {
  noteLetterPitchClasses,
  accidentalIntegers,
  noteLetterIntegers,
} from 'shared/definitions/musicTheory/notesAndAccidentals';
import Mousetrap from 'mousetrap';
import { findKey } from 'shared/src/utils/findKey';
import { defaultsDeep } from 'shared/src/utils/defaultsDeep';
import config from '../../config';
import utils from '../../utils';
import * as midiHelpers from '../../uTheory.Notation/ariaMidiHelpers';
import Note from '../../uTheory.Notation/note';
import type { ProcessedMidiMessage } from '../../uTheory.Notation/midiInput';
import MidiInput from '../../uTheory.Notation/midiInput';
import Interval from '../../uTheory.Notation/interval';
import KeySignature from '../../uTheory.Notation/keySignature';

const svgNS = 'http://www.w3.org/2000/svg';

type MidiNoteOrNoteString = number | Note | NoteName;

type MidiCallbackFunction = (midi: number, velocity?: number) => void;

interface NoteDescriptionInfo {
  midi: number;
  primaryLabel?: string;
  secondaryLabel?: string;
  highlighted?: boolean;
}

type AriaLiveStatus = 'assertive' | 'off' | 'polite' | 'alert';

export interface PianoSVGOptions {
  /**
   * The color that a highlighted note should be. Defaults to config.Colors.highlighted
   */
  pressedColor?: string;
  /**
   * The color that a highlighted black note should be. Defaults to
   * config.Colors.highlighted darkened to 0.75
   */
  pressedBlackColor?: string;
  /**
   * Whether listeners should be enabled on the keyboard, aka, whether it is
   * interactive. Also applies to MIDI input devices, unless overridden by
   * setting options.midiListeners.
   */
  listeners?: boolean;
  /**
   * Defaults to value of options.listeners, or false if not provided. Whether
   * to listen for input events from connected MIDI devices.
   */
  midiListeners?: boolean;

  lowestNote?: Note | string;
  highestNote?: Note | string;
  /**
   * Whether to highlight a note when a user presses it or not.
   */
  highlightPressed?: boolean;
  /**
   * String or function used to define alt text for screen readers on piano
   * elements
   */
  ariaTitle?:
    | string
    | ((descriptions: NoteDescriptionInfo[], lowestNote: Note, highestNote: Note) => string);
  /**
   * How to describe a note for aria. For D#/Eb that would be:
   *   - location: "The second of the pair of two black notes."
   *   - flats: "E-flat"
   *   - sharps: "D-sharp"
   *   - inclusive: 'D-sharp/E-flat'
   *   - KeySignature: picks the most likely note name for a note within that
   *     key signature. So, given a key signature of Bb major, would pick
   *     "E-flat" Default: inclusive
   */
  ariaNoteDescriptionType?: 'location' | 'flats' | 'sharps' | 'inclusive' | KeySignature;
  /**
   * The aria live level that should be applied to the piano title.
   */
  ariaLive?: AriaLiveStatus | (() => AriaLiveStatus);
}

export default class PianoSVG {
  /**
   * How to style highlighted notes. SVGElement attributes & values.
   */
  private highlightedStyle: Record<string, string> ={};

  /**
   * How to style black notes. SVGElement attributes & values.
   */
  private blackNoteStyle: Record<string, string>;

  /**
   * How to style white notes. SVGElement attributes & values.
   */
  private whiteNoteStyle: Record<string, string>;

  /**
   * The main label on a note. These exist for EVERY note that is
   * drawn, even if there is no text content in them.
   */
  private primaryLabels: { [midiNumber: number]: SVGTextElement } = {};

  /**
   * The secondary label on a note. These exist for EVERY note that is
   * drawn, even if there is no text content in them.
   */
  private secondaryLabels: { [midiNumber: number]: SVGTextElement } = {};

  /**
   * highlightedNotes
   */
  private highlightedNotes: { [midiNumber: number]: boolean } = {};

  /**
   * The text to label the piano with
   */
  private primaryLabelContents: { [midiNumber: number]: string } = {};

  /**
   * Styles to apply individually to primary labels:
   */
  private primaryLabelAttributeOverrides: { [midiNumber: number]: Record<string, string> } = {};

  /**
   * Styles to apply individually to secondary labels:
   */
  private secondaryLabelAttributeOverrides: { [midiNumber: number]: Record<string, string> } = {};

  /**
   * The text to label the piano with
   */
  private secondaryLabelContents: { [midiNumber: number]: string } = {};

  private svgObjects: { [midiNumber: number]: SVGPathElement } = {};

  private options: Partial<PianoSVGOptions>;

  lowestNote: Note;

  highestNote: Note;

  private lowest_midi: number;

  private highest_midi: number;

  /**
   * Scientific pitch notation octave number for the lowestNote on the piano.
   */
  private lowOctave!: number;

  /**
   * Scientific pitch notation octave number for the highestNote on the piano.
   */
  private highOctave!: number;

  /**
   * Where should the octaves begin from? If 0, C. 1, D. etc...
   */
  private shiftOctavesThisManyWhiteNotesLeft: number = 0;

  /**
   * Pixels to shift octaves to the left
   * offsetX === this.whiteNoteWidth * this.shiftOctavesThisManyWhiteNotesLeft
   */
  private offsetX!: number;

  private lowestWhiteNote!: Note;

  private highestWhiteNote!: Note;

  private totalWhiteNotes!: number;

  /**
   * The outer container element
   */
  private displayDiv: HTMLDivElement;

  /**
   * The container element for the SVG
   */
  private pianoDiv: HTMLDivElement;

  private noteOnListeners: MidiCallbackFunction[] = [];

  private noteOffListeners: MidiCallbackFunction[] = [];

  /**
   * Initialized if options.listeners is true.
   */
  private midiListener?: MidiInput;

  private oldAriaTitle?: string;

  private ariaTitleUpdater?: (title: string) => void;

  private svg!: SVGElement;

  private width!: number;

  private height?: number;

  private whiteNoteWidth!: number;

  private whiteNoteHeight!: number;

  private blackNoteWidth!: number;

  private blackNoteHeight!: number;

  private textSize!: {
    blackPrimary: number;
    blackSecondary: number;
    whitePrimary: number;
    whiteSecondary: number;
    whiteSolo: number;
  };

  /**
   * For uspizing or downsizing font when creating videos as needed.
   */
  videoExportCoefficient = 1;

  // Public constructor
  /**
   * A representation of a piano in SVG format for display or playing
   */
  constructor(displayDiv: HTMLDivElement, options: Partial<PianoSVGOptions> = {}) {
    // Configure Options:
    this.options = {};
    this.parsePressedColor(options.pressedColor);

    utils.createClass(
      'piano-svg-note-outlined',
      `stroke-width: 4px; stroke: ${config.Colors.selected};`
    );

    this.options.listeners = true;
    this.options.midiListeners = options.midiListeners ?? options.listeners;
    this.options.ariaLive = 'assertive';

    this.options = Object.assign(this.options, options);

    this.whiteNoteStyle = { stroke: 'black', strokeWidth: '1px', fill: 'white' };
    this.blackNoteStyle = { stroke: 'black', strokeWidth: '2px', fill: '#313131' };
    this.setPressedColor(this.options.pressedColor);

    this.lowestNote =
      typeof options.lowestNote !== 'undefined'
        ? new Note(options.lowestNote as NoteName)
        : new Note('C3');
    this.highestNote =
      typeof options.highestNote !== 'undefined'
        ? new Note(options.highestNote as NoteName)
        : new Note('C5');

    this.lowest_midi = this.lowestNote.getMidiNumber();
    this.highest_midi = this.highestNote.getMidiNumber();

    this.displayDiv = displayDiv;

    const pianoDiv = document.createElement('div');
    pianoDiv.style.width = '100%';

    displayDiv.appendChild(pianoDiv);
    this.pianoDiv = pianoDiv;

    this.calculateBoundaryNotes();
    this.draw();
    if (this.options.midiListeners ?? this.options.listeners) this.initializeMidi();
    if (this.options.listeners) this.initializeKeyboardListeners();
  }

  setPressedColor(pressedColor: string = config.Colors.highlighted) {
    this.highlightedStyle = {
      stroke: 'black',
      strokeWidth: '2px',
      // It is set definitely in this.parsePressedColor
      fill: pressedColor,
    };
  }

  private parsePressedColor(pressedColor: string = config.Colors.highlighted) {
    this.options.pressedColor = pressedColor;
    if (this.options.pressedColor.indexOf('#') === 0) {
      // it's a hex
      let r = parseInt(this.options.pressedColor.slice(1, 3), 16);
      let g = parseInt(this.options.pressedColor.slice(3, 5), 16);
      let b = parseInt(this.options.pressedColor.slice(5, 7), 16);
      r = Math.floor(r * 0.75);
      g = Math.floor(g * 0.75);
      b = Math.floor(b * 0.75);
      this.options.pressedBlackColor = `rgb(${r},${g},${b})`;
    }
  }

  setInteraction(isUIInteractive = false, isMIDIInteractive?: boolean) {
    isMIDIInteractive = isMIDIInteractive ?? isUIInteractive;

    if (isMIDIInteractive) this.initializeMidi();
    if (isUIInteractive) this.initializeKeyboardListeners();

    const keysDiv = this.displayDiv.querySelector('.aria-keys-div') as HTMLDivElement;
    if (keysDiv) {
      keysDiv.style.display = isUIInteractive ? 'block' : 'none';
    }
    const keyboardNavigationDiv = this.displayDiv.querySelector(
      '.piano-keyboard-navigation-instructions'
    ) as HTMLDivElement;
    if (keyboardNavigationDiv) {
      keyboardNavigationDiv.style.display = isUIInteractive ? 'block' : 'none';
    }
    const octaveAlertDiv = this.displayDiv.querySelector(
      '.piano-keyboard-octave-alert'
    ) as HTMLDivElement;
    if (octaveAlertDiv) {
      octaveAlertDiv.style.display = isUIInteractive ? 'block' : 'none';
    }

    this.options.listeners = isUIInteractive;
    this.options.midiListeners = isMIDIInteractive;
  }

  destroy() {
    this.setInteraction(false);
    this.options.ariaLive = 'off';
    if (this.midiListener) this.midiListener.stopListeners();
  }

  disableInteraction() {
    this.setInteraction(false);
  }

  enableInteraction() {
    this.setInteraction(true);
  }

  /**
   * Get the string to title the svg with
   *
   * @param {boolean} [doNotCallOptionsAriaTitle] use the built in version instead. Defaults false
   * @returns {string}
   */
  getAriaTitle(doNotCallOptionsAriaTitle = false) {
    const descriptions = this.getDescriptions();
    if (doNotCallOptionsAriaTitle || typeof this.options.ariaTitle !== 'function') {
      return this.describe(descriptions);
    }
    return this.options.ariaTitle(descriptions, this.lowestNote, this.highestNote);
  }

  setAriaLivePoliteness() {
    const titleEl = this.pianoDiv.querySelector('.aria-title-div') as HTMLDivElement;
    const ariaLive =
      typeof this.options.ariaLive === 'function'
        ? this.options.ariaLive()
        : this.options.ariaLive || 'assertive';
    if (ariaLive === 'off') {
      titleEl.removeAttribute('aria-live');
      titleEl.removeAttribute('role');
      return;
    }
    if (ariaLive === 'alert') {
      titleEl.removeAttribute('aria-live');
      titleEl.setAttribute('role', 'alert');
      return;
    }
    titleEl.setAttribute('aria-live', ariaLive);
  }

  /**
   * Pull information on what's highlighted, pressed, labeled, etc... and provide it to
   * a user-provided or default function to give logical output text. If the text matches
   * exactly what was previously there, don't say anything unless specified.
   *
   * @param {boolean=} [forceLiveAnnouncement] whether to speak if the text is an exact match
   * to what was previously there; default false
   */
  updateAriaTitle(forceLiveAnnouncement = false) {
    const title = this.getAriaTitle();

    // let titleElement = this.svg.getElementsByTagName('title')[0];
    let titleElement = this.pianoDiv.querySelector('.aria-title-div');
    if (!titleElement) {
      titleElement = document.createElement('div');
      titleElement.classList.add('aria-title-div', 'sr-only');
    }
    const noChange = this.oldAriaTitle === title;
    if (noChange && !forceLiveAnnouncement) return;
    this.ariaTitleUpdater =
      this.ariaTitleUpdater ||
      utils.debounce((newTitle: string) => {
        const titleEl = this.pianoDiv.querySelector('.aria-title-div') as HTMLDivElement;
        this.setAriaLivePoliteness();
        titleEl.innerHTML = newTitle;
        this.oldAriaTitle = newTitle;
      }, 250);
    this.ariaTitleUpdater(title);
  }

  /**
   *
   * @param {KeyDescription} descriptions
   * @returns {string} Accessible description of the current state of the keyboard.
   */
  describe(descriptions: NoteDescriptionInfo[] /* lowestNote, highestNote */) {
    const replacements = [
      ['bb', 'v'],
      ['b', ' flat'],
      ['v', ' double flat'],
      ['n', ' natural'],
      ['#', ' sharp'],
      ['x', ' double sharp'],
      ['__', 'blank'],
      ['_', 'blank'],
    ] as const;
    const replace = (str: string) =>
      replacements.reduce(
        (string, [needle, replacement]) => `${string}`.replace(needle, replacement),
        str
      );

    return (
      descriptions
        // .sort((a, b) => {
        //   const highlightSort = !!a.highlighted - !!b.highlighted;
        //   if (highlightSort) return highlightSort;
        //   return a.midi - b.midi;
        // })
        .map(({ midi, highlighted, primaryLabel, secondaryLabel }) => {
          const noteName =
            this.options.ariaNoteDescriptionType === 'location'
              ? this.describeMidi(midi)
              : replace(this.describeMidi(midi));
          const describers = [
            highlighted ? 'is highlighted' : false,
            primaryLabel ? `is labeled ${replace(primaryLabel)}` : false,
            secondaryLabel ? `is labeled ${replace(secondaryLabel)}` : false,
          ].filter((t) => t);
          const describerText = utils.listToString(describers);
          return `${noteName} ${describerText}`;
        })
        // .concat([
        //   `... the lowest visible piano note is ${replace(lowestNote.toString())}`,
        //   `... the highest visible piano note is ${replace(highestNote.toString())}`,
        // ])
        .join('; ')
    );
  }

  describeMidi(midi: number) {
    const noteDescriptionType = this.options.ariaNoteDescriptionType || 'inclusive';
    // 'location' | 'flats' | 'sharps' | 'inclusive' | KeySignature
    switch (noteDescriptionType) {
      case 'location':
        return midiHelpers.toLocationString(midi);
      case 'flats':
        return midiHelpers.toFlatName(midi);
      case 'sharps':
        return midiHelpers.toSharpName(midi);
      default:
        if (noteDescriptionType instanceof KeySignature) {
          return midiHelpers.toNameInKey(midi, noteDescriptionType);
        }
        return midiHelpers.toNoteNamesString(midi, true);
    }
  }

  /**
   * @returns {Array<NoteDescriptionInfo>} for current state of piano
   */
  getDescriptions(): NoteDescriptionInfo[] {
    const descriptions: NoteDescriptionInfo[] = [];
    const getOrCreateDescription = (midi: number): NoteDescriptionInfo => {
      const description = descriptions.find((d) => d.midi === midi);
      if (!description) {
        const newDescription = { midi };
        descriptions.push(newDescription);
        return newDescription;
      }
      return description;
    };

    this.getHighlightedNotes().forEach((midi) => {
      getOrCreateDescription(midi);
      getOrCreateDescription(midi).highlighted = true;
    });
    Object.entries(this.primaryLabelContents).forEach(([midi, text]) => {
      if (!text) return;
      getOrCreateDescription(Number(midi));
      getOrCreateDescription(Number(midi)).primaryLabel = text;
    });

    Object.entries(this.secondaryLabelContents).forEach(([midi, text]) => {
      if (!text) return;
      getOrCreateDescription(Number(midi));
      getOrCreateDescription(Number(midi)).secondaryLabel = text;
    });

    // Prioritize highlighted notes, then lowest to highest
    return descriptions.sort((a, b) => {
      if (a.highlighted && !b.highlighted) return -1;
      if (b.highlighted && !a.highlighted) return 1;
      return a.midi - b.midi;
    });
  }

  /**
   *
   * @param {'off' | 'polite' | 'assertive' | 'rude'} status
   */
  setAriaLiveStatus(status: AriaLiveStatus) {
    this.displayDiv.setAttribute('aria-live', status);
  }

  private midiInitialized = false;

  // Public Functions
  initializeMidi() {
    if (this.midiInitialized) return;
    if (this.midiListener) {
      this.midiListener.stopListeners();
    }

    this.midiListener = new MidiInput()
      .stopListeners() // First clear any old listeners
      .addListener(
        'noteOn',
        (message: ProcessedMidiMessage) => {
          this.noteOn(message.midiNumber as number, message.velocity);
        },
        this
      )
      .addListener('noteOff', (message: ProcessedMidiMessage) => {
        this.noteOff(message.midiNumber as number, message.velocity);
      });

    this.midiInitialized = true;
  }

  private keyboardInitialized = false;

  initializeKeyboardListeners() {
    if (this.keyboardInitialized) return;
    // eslint-disable-next-line no-confusing-arrow
    const wrap =
      (fn: Function, ...args: any[]) =>
      (evt: Mousetrap.ExtendedKeyboardEvent) =>
        !evt.repeat ? fn(...args) : undefined;
    let octave = 3;
    const infoDiv = document.createElement('div');
    const alertDiv = document.createElement('div');
    infoDiv.classList.add('piano-keyboard-navigation-instructions');
    alertDiv.classList.add('piano-keyboard-octave-alert');

    const changeOctave = (upOrDown: 'up' | 'down') => {
      octave += upOrDown === 'up' ? 1 : -1;

      infoDiv.innerHTML = `
        <h3>Keyboard navigation of piano inputs.</h3>
        <ul>
          <li>The bottom row of letters on the keyboard, z through m, are the notes C up to B on the piano</li>
          <li>the middle row of letters are the black notes -- between z and m is s, so this represents C-sharp or D-flat.
            Between x and c is d, so this represents D-sharp and E-flat, etc...</li>
          <li>The current octave is ${octave}. Except for scale exercises, the octave used to answer
          a question does not matter. To change octaves, use '<' to go down an octave, and '>' to move up an octave.</li>
          <li>If you have a midi keyboard plugged in, you may use that instead.</li>
        </ul>
      `;
      alertDiv.innerHTML = `octave ${octave}`;
    };

    alertDiv.classList.add('sr-only');
    infoDiv.classList.add('sr-only');

    changeOctave('up');
    this.displayDiv.appendChild(infoDiv);
    this.displayDiv.appendChild(alertDiv);
    alertDiv.setAttribute('aria-live', 'polite');

    const noteKeyShortcuts = [
      // key, note
      ['z', 'C'],
      ['x', 'D'],
      ['c', 'E'],
      ['v', 'F'],
      ['b', 'G'],
      ['n', 'A'],
      ['m', 'B'],
      [',', 'C 1'],
      ['.', 'D 1'],
      ['/', 'E 1'],
      ['s', 'C#'],
      ['d', 'Eb'],
      ['g', 'F#'],
      ['h', 'Ab'],
      ['j', 'Bb'],
      ['l', 'C# 1'],
      [';', 'Eb 1'],
    ] as const;

    const noteOnOff = (note: typeof noteKeyShortcuts[number][1], onOrOff: 'on' | 'off') => {
      const genericInt = noteLetterPitchClasses[note[0] as LetterName];
      const accidentalInt =
        note[1] && note[1] !== ' ' ? accidentalIntegers[note[1] as Accidental] : 0;
      const octavehigh = note[2] ? 1 : 0;
      const midiNumber = genericInt + accidentalInt + (octave + octavehigh) * 12;
      return onOrOff === 'on' ? this.noteOn(midiNumber) : this.noteOff(midiNumber);
    };

    noteKeyShortcuts.forEach(([key, note]) => {
      const on = wrap(noteOnOff, note, 'on');
      const off = wrap(noteOnOff, note, 'off');
      Mousetrap(this.displayDiv).bind(key, on, 'keydown');
      Mousetrap(this.displayDiv).bind(key, off, 'keyup');
    });
    Mousetrap(this.displayDiv).bind('<', wrap(changeOctave, 'down'), 'keydown');
    Mousetrap(this.displayDiv).bind('>', wrap(changeOctave, 'up'), 'keydown');

    this.keyboardInitialized = true;
  }

  setBoundaries(lowest: NoteName | Note, highest: NoteName | Note) {
    // make sure it's an octave:
    let lowestNote = Note.stringToNote(lowest);
    let highestNote = Note.stringToNote(highest);

    const interval = lowestNote.getGenericIntervalTo(highestNote);
    if (interval < 8) {
      const intToAdd = 1 + (8 - interval) / 2;
      lowestNote = lowestNote.getNoteAtGeneric(-Math.floor(intToAdd));
      highestNote = lowestNote.getNoteAtGeneric(8);
    }

    this.lowestNote = lowestNote;
    this.highestNote = highestNote;
    this.lowest_midi = midiHelpers.toMidi(lowestNote);
    this.highest_midi = midiHelpers.toMidi(highestNote);

    this.calculateBoundaryNotes();

    this.draw();
  }

  calculateBoundaryNotes() {
    // Calculates:
    //  lowOctave
    //  highOctave
    //  lowestWhiteNote
    //  highestWhiteNote
    //  totalWhiteNotes

    this.lowOctave = Math.floor(this.lowest_midi / 12);
    this.highOctave = Math.floor(this.highest_midi / 12);

    // Each chromatic note is transformed into 0-7 representing white notes:
    const midiToWhiteNoteHigh = [0, 1, 1, 2, 2, 3, 4, 4, 5, 5, 6, 6];
    const midiToWhiteNoteLow = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6];

    const lowestWhiteNoteInt = midiToWhiteNoteLow[this.lowest_midi % 12]!;
    const highestWhiteNoteInt = midiToWhiteNoteHigh[this.highest_midi % 12]!;

    const letterInts = noteLetterIntegers;
    const lowestLetter = findKey(letterInts, lowestWhiteNoteInt)!;
    const highestLetter = findKey(letterInts, highestWhiteNoteInt)!;

    this.shiftOctavesThisManyWhiteNotesLeft = lowestWhiteNoteInt;

    this.lowestWhiteNote = new Note((lowestLetter + this.lowOctave) as NoteName);
    this.highestWhiteNote = new Note((highestLetter + this.highOctave) as NoteName);

    this.totalWhiteNotes = Interval.getGenericInterval(this.lowestWhiteNote, this.highestWhiteNote);
  }

  draw() {
    // clear the canvas:
    this.svgObjects = {};
    this.primaryLabels = {};
    this.secondaryLabels = {};

    // remove the SVG:
    const oldSvgs = this.pianoDiv.getElementsByTagName('svg');
    if (oldSvgs[0]) this.pianoDiv.removeChild(oldSvgs[0]);

    // add the title element if we need to:
    let titleDiv = this.pianoDiv.querySelector('.aria-title-div');
    if (!titleDiv) {
      titleDiv = document.createElement('div');
      titleDiv.classList.add('aria-title-div', 'sr-only');
      this.pianoDiv.appendChild(titleDiv);
    }
    this.updateAriaTitle();

    if (this.options.listeners) this.initializeAriaPianoKeys();
    // create the SVG:
    // eslint-disable-next-line no-undef
    const svg = document.createElementNS(svgNS, 'svg');
    this.svg = svg;

    this.svg.setAttribute('aria-hidden', 'true');
    // const titleElement = document.createElementNS(svgNS, 'title');
    // titleElement.textContent =
    // this.svg.appendChild(titleElement);
    this.width = this.displayDiv.scrollWidth - 1;
    if (this.width === 0) this.width = 500;

    this.whiteNoteWidth = Math.floor(this.width / this.totalWhiteNotes);
    this.whiteNoteHeight = this.whiteNoteWidth * 5;

    // if that's taller than our height, re-scale based on height:
    if (typeof this.height !== 'undefined' && this.whiteNoteHeight > this.height) {
      //        console.log("Resizing piano for height! " + this.height);
      this.whiteNoteHeight = this.height - 2;
      this.whiteNoteWidth = this.whiteNoteHeight / 5;
    } else if (
      this.displayDiv.clientHeight !== 0 &&
      this.whiteNoteHeight > this.displayDiv.clientHeight
    ) {
      this.whiteNoteHeight = this.displayDiv.clientHeight - 2;
      this.whiteNoteWidth = this.whiteNoteHeight / 5;
    }

    //      this.blackNoteWidth = this.whiteNoteWidth * 0.602;
    this.blackNoteWidth = this.whiteNoteWidth * 0.552;
    this.blackNoteHeight = this.whiteNoteHeight * 0.666;

    this.blackNoteStyle.strokeWidth = `${this.blackNoteWidth / 20}px`;

    this.width = this.whiteNoteWidth * this.totalWhiteNotes;
    svg.setAttribute('width', `${this.width + 1}`);

    svg.setAttribute('height', `${this.whiteNoteHeight}px`);

    svg.setAttribute('viewBox', `0 0 ${this.width} ${this.whiteNoteHeight}`);

    this.whiteNoteHeight--;

    this.offsetX = -this.whiteNoteWidth * this.shiftOctavesThisManyWhiteNotesLeft;

    for (let i = this.lowOctave; i <= this.highOctave; i++) this.drawOctave(i);

    this.pianoDiv.appendChild(this.svg);
    //      return this.height - this.whiteNoteHeight - 2;
    const displayDivHeight = this.whiteNoteHeight + 2;
    this.displayDiv.style.height = `${displayDivHeight}px`;
    return this.displayDiv.offsetHeight;
  }

  initializeAriaPianoKeys() {
    let keysDiv = this.pianoDiv.querySelector<HTMLDivElement>('.aria-keys-div');
    if (!keysDiv) {
      keysDiv = document.createElement('div');
      keysDiv.classList.add('aria-keys-div', 'sr-only');
      keysDiv.innerHTML = '<h3>Piano Key Buttons</h3>';
      this.pianoDiv.appendChild(keysDiv);
    }
    keysDiv.innerHTML = '<h3>Piano Key Buttons</h3>';
    keysDiv.setAttribute('role', 'group');
    for (let midi = this.lowest_midi; midi <= this.highest_midi; midi++) {
      this.makePianoKey(midi, keysDiv);
    }
  }

  makePianoKey(midi: number, keysDiv: HTMLDivElement) {
    const noteNamesString = this.describeMidi(midi);

    const button = document.createElement('button');
    button.classList.add('sr-only');
    button.innerHTML = noteNamesString;
    button.addEventListener('click', () => {
      this.noteOn(midi);
      setTimeout(() => this.noteOff(midi), 200);
    });
    button.addEventListener('focusin', () => {
      this.outlineNote(midi);
    });
    button.addEventListener('focusout', () => {
      this.clearNoteOutline(midi);
    });
    keysDiv.appendChild(button);
  }

  resize(oferredHeight: number) {
    //      console.log("oferred: " + oferredHeight);
    this.height = oferredHeight;
    // this.draw returns any unused height
    const usedHeight = this.draw();
    //      console.log("unused: " + unusedHeight);

    this.height = usedHeight;
    return oferredHeight - usedHeight;
  }

  drawOctave(octave: number) {
    const whiteNoteMidis = [0, 2, 4, 5, 7, 9, 11] as const;
    const blackNoteMidis = [1, 3, 6, 8, 10] as const;
    // x positioning of blackNote as a ratio of white note.
    const blackNoteOffsets = [0.95, 2.05, 3.95, 5, 6.05] as const;
    const visibleOctave = octave - this.lowOctave;
    const octaveWidth = this.whiteNoteWidth * 7;
    // console.log("octaveWidth: " + octaveWidth);
    const thisOctaveOffset = this.offsetX + visibleOctave * octaveWidth;

    let x;
    let midi;
    // Calculate text sizes:
    this.calculateTextSize();

    // Draw the white notes:
    whiteNoteMidis.forEach((zeroOctaveMidi, i) => {
      x = this.whiteNoteWidth * i + thisOctaveOffset;
      midi = zeroOctaveMidi + octave * 12;
      this.drawWhiteNote(midi, x);
    });

    // Draw the black notes:
    blackNoteMidis.forEach((zeroOctaveMidi, i) => {
      x = blackNoteOffsets[i]! * this.whiteNoteWidth - this.blackNoteWidth / 2 + thisOctaveOffset;
      midi = zeroOctaveMidi + octave * 12;
      this.drawBlackNote(midi, x);
    });
  }

  /**
   * Draw a white note at starting position x pixels.
   */
  drawWhiteNote(midi: number, x: number) {
    const options = {};
    utils.merge(options, this.whiteNoteStyle);

    // console.log("Drawing: " + midi + " Highlighted: " + this.highlightedNotes[midi]);

    if (this.highlightedNotes[midi] === true) {
      utils.merge(options, this.highlightedStyle);
    }

    const rect = this.drawRect(x, 0, this.whiteNoteWidth, this.whiteNoteHeight - 1, options);
    this.svg.appendChild(rect);

    const text = this.drawText(midi, x, false, false);
    this.svg.appendChild(text);
    this.primaryLabels[midi] = text;

    // Draw secondary label:
    const text2 = this.drawText(midi, x, false, true);
    this.svg.appendChild(text2);
    this.secondaryLabels[midi] = text2;

    if (this.options.listeners) this.addEventListeners(rect, midi);
    this.svgObjects[midi] = rect;
  }

  /**
   * Draw a black note with midi number midi at pixel x.
   */
  drawBlackNote(midi: number, x: number) {
    const options = {};
    utils.merge(options, this.blackNoteStyle);

    if (this.highlightedNotes[midi] === true) {
      utils.merge(options, this.highlightedStyle);
    }
    const rect = this.drawRect(x, -2, this.blackNoteWidth, this.blackNoteHeight + 2, options);
    if (this.options.listeners) this.addEventListeners(rect, midi);
    this.svgObjects[midi] = rect;
    this.svg.appendChild(rect);

    const text = this.drawText(midi, x, true, false);
    this.svg.appendChild(text);
    this.primaryLabels[midi] = text;

    const text2 = this.drawText(midi, x, true, true);
    this.svg.appendChild(text2);
    this.secondaryLabels[midi] = text2;
  }

  calculateTextSize() {
    const videoExportCoefficient = this.videoExportCoefficient; // for upsizing fonts in recording
    // videos.

    const secondaryFontCoeff = 0.7;
    const blackFontSize = this.blackNoteWidth / 1.3;
    const whiteFontSize = this.whiteNoteWidth / 1.7;
    const whiteSecondaryFontSize = (this.whiteNoteWidth * secondaryFontCoeff) / 1.7;

    this.textSize = {
      blackPrimary: blackFontSize * videoExportCoefficient,
      blackSecondary: blackFontSize * videoExportCoefficient,
      whitePrimary: whiteFontSize * videoExportCoefficient,
      whiteSecondary: whiteSecondaryFontSize * videoExportCoefficient,
      whiteSolo: (this.whiteNoteWidth * videoExportCoefficient) / 1.3,
    };
  }

  drawText(midi: number, x: number, blackNote: boolean, secondaryLabel: boolean) {
    // eslint-disable-next-line no-nested-ternary
    const fontSize = blackNote
      ? this.textSize.blackPrimary
      : secondaryLabel
      ? this.textSize.whiteSecondary
      : this.textSize.whitePrimary;

    //      console.log("whiteNoteWidth: " + this.whiteNoteWidth);

    let y;
    let fill;
    const primaryOrSecondaryOffset = secondaryLabel ? -0.15 : 0;
    //    const secondaryFontCoeff = (secondaryLabel) ? 0.7 : 1;
    if (blackNote) {
      // eslint-disable-next-line no-param-reassign
      x += this.blackNoteWidth / 2;
      y = this.blackNoteHeight * (0.85 + primaryOrSecondaryOffset * 1.3);

      if (this.highlightedNotes[midi] === true) {
        fill = '#313131';
      } else {
        fill = '#DDDDDD';
      }
    } else {
      // it's a white note
      // eslint-disable-next-line no-param-reassign
      x += this.whiteNoteWidth / 2;
      y = this.whiteNoteHeight * (0.9 + primaryOrSecondaryOffset);
      fill = '#313131';
    }

    const options = {
      'text-anchor': 'middle',
      'font-family': 'uTheory Musical',
      x: `${x}`,
      y: `${y}`,
      'font-size': `${fontSize}px`,
      fill,
      class: 'accidentals',
    };

    const optionsOverride = secondaryLabel
      ? this.secondaryLabelAttributeOverrides[midi]
      : this.primaryLabelAttributeOverrides[midi];

    // eslint-disable-next-line no-undef
    const text = document.createElementNS(svgNS, 'text');
    applyAttributes(text, defaultsDeep(optionsOverride, options));

    const labelContents = secondaryLabel ? this.secondaryLabelContents : this.primaryLabelContents;

    if (typeof labelContents[midi] !== 'undefined') {
      text.textContent = labelContents[midi] || null;
    }

    return text;
  }

  /**
   * Draw a rectangle
   * @param x - x position
   * @param y - y position
   * @param w - width
   * @param h - height
   * @param options - SVG attributes record to apply to created element
   */
  drawRect(
    x: number,
    y: number,
    w: number,
    h: number,
    options: Record<string, string>
  ): SVGPathElement {
    interface Point {
      x: number;
      y: number;
    }
    interface MoveCommand extends Point {
      command: 'M';
    }
    interface LineCommand extends Point {
      command: 'L';
    }
    interface CurveCommand extends Point {
      beziers: [Point, Point];
      command: 'C';
    }
    const commands: (MoveCommand | LineCommand | CurveCommand)[] = [
      { x: 0, y: 0, command: 'M' },
      { x: 0, y: 95, command: 'L' },
      {
        x: 10,
        y: 100,
        command: 'C',
        beziers: [
          { x: 0, y: 100 },
          { x: 0, y: 100 },
        ],
      },
      { x: 90, y: 100, command: 'L' },
      {
        x: 100,
        y: 95,
        command: 'C',
        beziers: [
          { x: 100, y: 100 },
          { x: 100, y: 100 },
        ],
      },
      { x: 100, y: 0, command: 'L' },
      { x: 0, y: 0, command: 'L' },
    ];

    let pathString = '';
    let spacer = '';

    function translate<T extends Point | LineCommand | CurveCommand | MoveCommand>(point: T): T {
      // eslint-disable-next-line no-param-reassign, no-mixed-operators
      const newX = (point.x * w) / 100 + x;
      // eslint-disable-next-line no-param-reassign, no-mixed-operators
      const newY = (point.y * h) / 100 + y;
      if ('command' in point && point.command === 'C') {
        return {
          ...point,
          x: newX,
          y: newY,
          beziers: point.beziers.map(translate),
        };
      }
      return {
        ...point,
        x: newX,
        y: newY,
      };
    }

    commands.map(translate).forEach((translatedCommand) => {
      if (translatedCommand.command === 'C') {
        pathString += `${spacer}C ${translatedCommand.beziers[0].x} ${translatedCommand.beziers[0].y}, ${translatedCommand.beziers[1].x} ${translatedCommand.beziers[1].y}, ${translatedCommand.x} ${translatedCommand.y}`;
      } else {
        pathString += `${spacer + translatedCommand.command} ${translatedCommand.x} ${
          translatedCommand.y
        }`;
      }
      spacer = ' ';
    }, this);

    // eslint-disable-next-line no-undef
    const path = document.createElementNS(svgNS, 'path');
    path.setAttributeNS(null, 'd', pathString);

    applyAttributes(path, options);

    return path;
  }

  /**
   * Apply specified SVG attributes to an SVG element
   */
  addEventListeners(element: SVGPathElement, midiNumber: number) {
    const that = this;
    element.addEventListener(
      'touchstart',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        that.noteOn(midiNumber);
      },
      false
    );

    element.addEventListener(
      'touchend',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        that.noteOff(midiNumber);
      },
      false
    );

    element.addEventListener(
      'touchcancel',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        that.noteOff(midiNumber);
      },
      false
    );

    element.addEventListener(
      'mousedown',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        if (e.detail !== 1) return;
        that.noteOn(midiNumber);
      },
      false
    );

    element.addEventListener(
      'mouseup',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        that.noteOff(midiNumber);
      },
      false
    );

    element.addEventListener(
      'mouseout',
      (e) => {
        if (that.noteOnListeners.length > 0) {
          e.preventDefault();
        }
        that.noteOff(midiNumber);
      },
      false
    );
  }

  setNoteOutline(midiNoteOrNoteString: MidiNoteOrNoteString, isOutlined: boolean) {
    const midiNumber = midiHelpers.toMidi(midiNoteOrNoteString);
    const el = this.svgObjects[midiNumber];
    if (el) {
      if (isOutlined) el.classList.add('piano-svg-note-outlined');
      else el.classList.remove('piano-svg-note-outlined');
    }
  }

  outlineNote(midiNoteOrNoteString: MidiNoteOrNoteString) {
    this.setNoteOutline(midiNoteOrNoteString, true);
  }

  clearNoteOutline(midiNoteOrNoteString: MidiNoteOrNoteString) {
    this.setNoteOutline(midiNoteOrNoteString, false);
  }

  /**
   * @param {string | number | Note} midiNoteOrNoteString
   * @param {string} color
   * @param doNotUpdateAria
   */
  colorNote(
    midiNoteOrNoteString: MidiNoteOrNoteString,
    color: string | undefined = undefined,
    doNotUpdateAria = false
  ) {
    // console.log("Coloring: " + midi_number_or_note);
    const midiNumber = midiHelpers.toMidi(midiNoteOrNoteString);
    const previouslyHighlighted = !!this.highlightedNotes[midiNumber];
    if (typeof this.svgObjects[midiNumber] !== 'undefined') {
      const options = {
        ...this.highlightedStyle,
      };

      if (typeof color !== 'undefined') {
        options.fill = color;
      }

      // If it's a black note, invert the text label color
      if (!isWhiteNote(midiNumber)) {
        applyAttributes(this.primaryLabels[midiNumber]!, { fill: '#313131' });
        applyAttributes(this.secondaryLabels[midiNumber]!, { fill: '#313131' });
      }

      this.highlightedNotes[midiNumber] = true;

      applyAttributes(this.svgObjects[midiNumber]!, options);
    }
    if (!previouslyHighlighted && !doNotUpdateAria) this.updateAriaTitle();
  }

  /* clearNoteColoring
   * @param anything: midinumber, note, or string evaluating to note
   *
   */
  clearNoteColoring(midiNoteOrNoteString: MidiNoteOrNoteString, suppressAriaUpdate = false) {
    const midiNumber = midiHelpers.toMidi(midiNoteOrNoteString);
    const previouslyHighlighted = !!this.highlightedNotes[midiNumber];
    if (typeof this.svgObjects[midiNumber] !== 'undefined') {
      this.highlightedNotes[midiNumber] = false;

      const isWhite = isWhiteNote(midiNumber);
      if (isWhite) {
        applyAttributes(this.svgObjects[midiNumber]!, this.whiteNoteStyle);
      } else {
        applyAttributes(this.primaryLabels[midiNumber]!, { fill: '#DDDDDD' });
        applyAttributes(this.secondaryLabels[midiNumber]!, { fill: '#DDDDDD' });
        applyAttributes(this.svgObjects[midiNumber]!, this.blackNoteStyle);
      }
    }
    if (previouslyHighlighted && !suppressAriaUpdate) this.updateAriaTitle();
  }

  clearColors() {
    Object.entries(this.highlightedNotes).forEach(([midi, value]) => {
      if (value === true) {
        this.clearNoteColoring(Number(midi), true);
      }
    });

    this.highlightedNotes = {};
  }

  toggleNote(midiNoteOrNoteString: MidiNoteOrNoteString) {
    const midiNumber = midiHelpers.toMidi(midiNoteOrNoteString);
    if (
      typeof this.highlightedNotes[midiNumber] !== 'undefined' &&
      this.highlightedNotes[midiNumber] === true
    ) {
      this.highlightedNotes[midiNumber] = false;
      this.clearNoteColoring(midiNumber);
    } else {
      this.highlightedNotes[midiNumber] = true;
      this.colorNote(midiNumber);
    }
  }

  getHighlightedNotes() {
    return Object.entries(this.highlightedNotes)
      .filter(([, value]) => value)
      .map(([midi]) => Number(midi));
  }

  labelNote(
    midiNoteOrNoteString: MidiNoteOrNoteString,
    text: string,
    secondaryLabel?: boolean,
    styleObject?: Record<string, string>
  ) {
    const midi = midiHelpers.toMidi(midiNoteOrNoteString);
    const labelsObject = secondaryLabel ? this.secondaryLabels : this.primaryLabels;
    const labelContents = secondaryLabel ? this.secondaryLabelContents : this.primaryLabelContents;

    console.log('midi', midi);
    if (typeof labelsObject === 'undefined' || typeof labelsObject[midi] === 'undefined') {
      throw Error(
        `NaL: ${'The label object is undefined.\nMidi Number: '}${midi}\nSecondary: ${secondaryLabel}`
      );
    }
    labelsObject[midi]!.textContent = text;
    labelContents[midi] = text;

    if (styleObject) {
      applyAttributes(labelsObject[midi]!, styleObject);
      if (secondaryLabel) {
        this.secondaryLabelAttributeOverrides[midi] = styleObject;
      } else {
        this.primaryLabelAttributeOverrides[midi] = styleObject;
      }
    }

    // Set the font size based on whether another label is present:
    // don't worry about black notes, always same size
    if (isWhiteNote(midi)) {
      if (this.hasPrimaryLabel(midi) && this.hasSecondaryLabel(midi)) {
        // Set both primary & secondary to their sizes.
        applyAttributes(this.primaryLabels[midi]!, {
          'font-size': `${this.textSize.whitePrimary}px`,
        });
        applyAttributes(this.secondaryLabels[midi]!, {
          'font-size': `${this.textSize.whiteSecondary}px`,
        });
      } else {
        applyAttributes(labelsObject[midi]!, { 'font-size': `${this.textSize.whiteSolo}px` });
      }
    }
    this.updateAriaTitle();
  }

  /* clearNoteLabel
   * @param midinumber, note, or string evaluating to note
   */
  clearNoteLabel(midiNoteOrNoteString: MidiNoteOrNoteString, suppressAriaUpdate?: boolean) {
    const midi = midiHelpers.toMidi(midiNoteOrNoteString);
    if (
      typeof this.primaryLabels !== 'undefined' &&
      typeof this.primaryLabels[midi] !== 'undefined'
    ) {
      this.primaryLabels[midi]!.textContent = null;
      delete this.primaryLabelContents[midi];
      delete this.secondaryLabelAttributeOverrides[midi];
    }
    if (
      typeof this.secondaryLabels !== 'undefined' &&
      typeof this.secondaryLabels[midi] !== 'undefined'
    ) {
      this.secondaryLabels[midi]!.textContent = null;
      delete this.secondaryLabelContents[midi];
      delete this.secondaryLabelAttributeOverrides[midi];
    }
    if (!suppressAriaUpdate) this.updateAriaTitle();
  }

  clearNoteLabels() {
    Object.keys(this.primaryLabelContents).forEach((midi) => {
      this.clearNoteLabel(Number(midi));
    });

    Object.keys(this.secondaryLabelContents).forEach((midi) => {
      this.clearNoteLabel(Number(midi));
    });
    this.highlightedNotes = {};
  }

  hasPrimaryLabel(midiNoteOrNoteString: MidiNoteOrNoteString) {
    const midi = midiHelpers.toMidi(midiNoteOrNoteString);
    return typeof this.primaryLabelContents[midi] !== 'undefined';
  }

  hasSecondaryLabel(midiNoteOrNoteString: MidiNoteOrNoteString) {
    const midi = midiHelpers.toMidi(midiNoteOrNoteString);
    return typeof this.secondaryLabelContents[midi] !== 'undefined';
  }

  removeListeners() {
    // Removes all listeners -- as when a game is over and we
    // want to be sure no future clicks register:
    const newElement = this.svg.cloneNode(true);
    this.svg.parentNode?.replaceChild(newElement, this.svg);
    if (this.midiListener) this.midiListener.stopListeners();
  }

  removeSVGListeners() {
    this.noteOnListeners = [];
    this.noteOffListeners = [];
  }

  noteOn(midiNumber: number, velocity?: number) {
    this.noteOnListeners.forEach((value) => {
      value(midiNumber, velocity);
    });
    if (this.options.highlightPressed) {
      this.colorNote(midiNumber);
    }
  }

  noteOff(midiNumber: number, velocity?: number) {
    this.noteOffListeners.forEach((value) => {
      value(midiNumber, velocity);
    });
    if (this.options.highlightPressed) {
      this.clearNoteColoring(midiNumber);
    }
  }

  addNoteOnListener(callback: MidiCallbackFunction, thisArg: any = this) {
    // adds function to be called back when status changes.
    this.noteOnListeners.push(callback.bind(thisArg));
  }

  addNoteOffListener(callback: MidiCallbackFunction, thisArg: any = this) {
    // adds function to be called back when note goes to off
    this.noteOffListeners.push(callback.bind(thisArg));
  }

  exportSVG() {
    // eslint-disable-next-line no-undef
    const s = new window.XMLSerializer();
    return s.serializeToString(this.svg);
  }

  downloadPNG(fileName: string) {
    if (typeof window === 'undefined')
      throw new Error('PNG export is only available in the browser environment.');
    const data = this.exportSVG();
    const canvas = document.createElement('canvas');
    canvas.style.width = `${this.width * 5}px`;
    canvas.style.height = `${this.whiteNoteHeight * 5}px`;
    canvas.width = this.width * 5;
    canvas.height = this.whiteNoteHeight * 5;
    console.log('canvas.width', canvas.width, 'canvas.height', canvas.height, this.whiteNoteHeight);
    function triggerDownload(imgURI: string) {
      const evt = new window.MouseEvent('click', {
        view: window,
        bubbles: false,
        cancelable: true,
      });

      const a = document.createElement('a');
      a.setAttribute('download', `${fileName || 'utheory-sandbox'}.png`);
      a.setAttribute('href', imgURI);
      a.setAttribute('target', '_blank');

      a.dispatchEvent(evt);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw Error('Cannot get 2d context from canvas.');
    }
    const DOMURL = window.URL || window.webkitURL;

    const img = new window.Image();
    const svgBlob = new window.Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    const url = DOMURL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      DOMURL.revokeObjectURL(url);

      const imgURI = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');

      triggerDownload(imgURI);
    };

    img.src = url;
  }
}

function applyAttributes(element: SVGElement, attributes: Record<string, string>) {
  Object.entries(attributes).forEach(([propertyName, value]) => {
    element.setAttributeNS(null, propertyName, value);
  });
}

function isWhiteNote(midiNumber: number) {
  const midi = midiNumber % 12;
  return (
    midi === 0 || midi === 2 || midi === 4 || midi === 5 || midi === 7 || midi === 9 || midi === 11
  );
}
